import { useMemo, useState } from 'react'
import { Download, Search, ShieldAlert } from 'lucide-react'
import { EmptyState } from '../ui/EmptyState'
import { TableRowSkeleton } from '../ui/Skeleton'
import { useKeywordIntel } from '../../hooks/useCompetitiveData'
import { exportToCSV } from '../../lib/export-utils'
import type { KeywordSignalRow, KeywordSqpRow } from '../../types/competitive'

type SortDir = 'asc' | 'desc'
type SqpSortCol = 'keyword' | 'rank' | 'brandShare' | 'competitorShare' | 'topCompetitor'
type SignalSortCol = 'keyword' | 'brand' | 'title' | 'rank' | 'firstSeen' | 'appearanceCount'

function formatDate(value: string | null): string {
  if (!value) {
    return '—'
  }

  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatPercent(value: number | null): string {
  if (value === null || Number.isNaN(value)) {
    return '—'
  }
  return `${value.toFixed(1)}%`
}

function shareToneClasses(value: number | null): string {
  if (value === null || Number.isNaN(value)) {
    return 'border border-slate-200 bg-slate-100 text-slate-700'
  }

  if (value >= 80) {
    return 'border border-emerald-200 bg-emerald-50 text-emerald-700'
  }

  if (value >= 50) {
    return 'border border-amber-200 bg-amber-50 text-amber-700'
  }

  return 'border border-red-200 bg-red-50 text-red-700'
}

function shareBarColor(value: number | null): string {
  if (value === null) return 'bg-slate-300'
  if (value >= 80) return 'bg-emerald-500'
  if (value >= 50) return 'bg-blue-500'
  if (value >= 25) return 'bg-amber-500'
  return 'bg-red-500'
}

function competitorShareBarColor(value: number | null): string {
  if (value === null || value === 0) return 'bg-slate-200'
  if (value >= 20) return 'bg-red-400'
  if (value >= 10) return 'bg-orange-400'
  return 'bg-amber-300'
}

function truncateText(value: string | null, maxLength: number): string {
  if (!value) {
    return '—'
  }
  if (value.length <= maxLength) {
    return value
  }
  return `${value.slice(0, Math.max(1, maxLength - 1)).trimEnd()}…`
}

function isWithinLastDays(value: string | null, days: number): boolean {
  if (!value) {
    return false
  }

  const timestamp = new Date(value).getTime()
  if (Number.isNaN(timestamp)) {
    return false
  }

  const elapsed = Date.now() - timestamp
  return elapsed >= 0 && elapsed <= days * 24 * 60 * 60 * 1000
}

function toSortableNumber(value: number | null): number {
  if (value === null || Number.isNaN(value)) {
    return Number.NEGATIVE_INFINITY
  }
  return value
}

function compareNumbers(left: number, right: number, dir: SortDir): number {
  return dir === 'asc' ? left - right : right - left
}

function compareStrings(left: string, right: string, dir: SortDir): number {
  const diff = left.localeCompare(right)
  return dir === 'asc' ? diff : -diff
}

function clampPercentage(value: number | null): number {
  if (value === null || Number.isNaN(value)) {
    return 0
  }
  return Math.max(0, Math.min(100, value))
}

interface SortableHeaderProps<T extends string> {
  label: string
  column: T
  activeColumn: T
  direction: SortDir
  onToggle: (column: T) => void
}

function SortableHeader<T extends string>({
  label,
  column,
  activeColumn,
  direction,
  onToggle,
}: SortableHeaderProps<T>) {
  const isActive = column === activeColumn

  return (
    <th className="px-3 py-2 font-medium">
      <button
        type="button"
        onClick={() => onToggle(column)}
        className="inline-flex items-center gap-1 text-left hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
      >
        {label}
        {isActive ? <span>{direction === 'asc' ? '▲' : '▼'}</span> : null}
      </button>
    </th>
  )
}

export default function KeywordIntelTab() {
  const { data, isLoading, error } = useKeywordIntel()
  const [sqpSortCol, setSqpSortCol] = useState<SqpSortCol>('rank')
  const [sqpSortDir, setSqpSortDir] = useState<SortDir>('asc')
  const [signalSortCol, setSignalSortCol] = useState<SignalSortCol>('keyword')
  const [signalSortDir, setSignalSortDir] = useState<SortDir>('asc')

  const keywordSqp = useMemo(() => {
    if (!data || !Array.isArray(data.sqp)) {
      return []
    }

    return data.sqp.map((rawRow: KeywordSqpRow) => {
      const rankValue = Number(rawRow.rank)
      const brandShareValue = Number(rawRow.brandShare)
      const competitorShareValue = Number(rawRow.competitorShare)
      const topCompetitorShareValue = rawRow.topCompetitor ? Number(rawRow.topCompetitor.share) : Number.NaN

      return {
        keyword: String(rawRow.keyword ?? ''),
        rank: Number.isFinite(rankValue) ? rankValue : null,
        brandShare: Number.isFinite(brandShareValue) ? brandShareValue : null,
        competitorShare: Number.isFinite(competitorShareValue) ? competitorShareValue : null,
        weekStart: rawRow.weekStart ? String(rawRow.weekStart) : null,
        topCompetitor: rawRow.topCompetitor
          ? {
              asin: rawRow.topCompetitor.asin ? String(rawRow.topCompetitor.asin) : null,
              name: rawRow.topCompetitor.name ? String(rawRow.topCompetitor.name) : null,
              share: Number.isFinite(topCompetitorShareValue) ? topCompetitorShareValue : null,
            }
          : null,
      }
    })
  }, [data])

  const keywordSignals = useMemo(() => {
    if (!data || !Array.isArray(data.keywordSignals)) {
      return []
    }

    return data.keywordSignals.map((rawRow: KeywordSignalRow) => {
      const rankValue = Number(rawRow.rank)
      const appearanceCountValue = Number(rawRow.appearanceCount)
      const rawRowWithSnakeCase = rawRow as KeywordSignalRow & { is_own_listing?: boolean }

      return {
        keyword: String(rawRow.keyword ?? ''),
        asin: rawRow.asin ? String(rawRow.asin) : null,
        brand: rawRow.brand ? String(rawRow.brand) : null,
        title: rawRow.title ? String(rawRow.title) : null,
        rank: Number.isFinite(rankValue) ? rankValue : null,
        firstSeen: rawRow.firstSeen ? String(rawRow.firstSeen) : null,
        appearanceCount: Number.isFinite(appearanceCountValue) ? appearanceCountValue : 0,
        isNewAdvertiser: Boolean(rawRow.isNewAdvertiser),
        isOwnListing: Boolean(rawRow.isOwnListing ?? rawRowWithSnakeCase.is_own_listing),
      }
    })
  }, [data])

  const sortedKeywordSqp = useMemo(() => {
    const rows = [...keywordSqp]

    rows.sort((left, right) => {
      if (sqpSortCol === 'keyword') {
        return compareStrings(left.keyword, right.keyword, sqpSortDir)
      }

      if (sqpSortCol === 'rank') {
        const leftRank = left.rank ?? Number.MAX_SAFE_INTEGER
        const rightRank = right.rank ?? Number.MAX_SAFE_INTEGER
        return compareNumbers(leftRank, rightRank, sqpSortDir)
      }

      if (sqpSortCol === 'brandShare') {
        return compareNumbers(toSortableNumber(left.brandShare), toSortableNumber(right.brandShare), sqpSortDir)
      }

      if (sqpSortCol === 'competitorShare') {
        return compareNumbers(
          toSortableNumber(left.competitorShare),
          toSortableNumber(right.competitorShare),
          sqpSortDir
        )
      }

      const leftTopCompetitor = left.topCompetitor?.name || left.topCompetitor?.asin || ''
      const rightTopCompetitor = right.topCompetitor?.name || right.topCompetitor?.asin || ''
      return compareStrings(leftTopCompetitor, rightTopCompetitor, sqpSortDir)
    })

    return rows
  }, [keywordSqp, sqpSortCol, sqpSortDir])

  const sortedKeywordSignals = useMemo(() => {
    const rows = [...keywordSignals]

    rows.sort((left, right) => {
      const ownListingSort = Number(Boolean(left.isOwnListing)) - Number(Boolean(right.isOwnListing))
      if (ownListingSort !== 0) {
        return ownListingSort
      }

      if (signalSortCol === 'keyword') {
        return compareStrings(left.keyword, right.keyword, signalSortDir)
      }

      if (signalSortCol === 'brand') {
        return compareStrings(left.brand || '', right.brand || '', signalSortDir)
      }

      if (signalSortCol === 'title') {
        return compareStrings(left.title || '', right.title || '', signalSortDir)
      }

      if (signalSortCol === 'rank') {
        const leftRank = left.rank ?? Number.MAX_SAFE_INTEGER
        const rightRank = right.rank ?? Number.MAX_SAFE_INTEGER
        return compareNumbers(leftRank, rightRank, signalSortDir)
      }

      if (signalSortCol === 'firstSeen') {
        const leftTime = left.firstSeen ? new Date(left.firstSeen).getTime() : 0
        const rightTime = right.firstSeen ? new Date(right.firstSeen).getTime() : 0
        return compareNumbers(leftTime, rightTime, signalSortDir)
      }

      return compareNumbers(left.appearanceCount, right.appearanceCount, signalSortDir)
    })

    return rows
  }, [keywordSignals, signalSortCol, signalSortDir])

  const competitorIntruders = useMemo(
    () => sortedKeywordSignals.filter((row) => !row.isOwnListing),
    [sortedKeywordSignals]
  )
  const ownListings = useMemo(
    () => sortedKeywordSignals.filter((row) => row.isOwnListing),
    [sortedKeywordSignals]
  )

  const keywordError = error instanceof Error ? error.message : null

  const toggleSqpSort = (column: SqpSortCol) => {
    if (column === sqpSortCol) {
      setSqpSortDir((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }

    setSqpSortCol(column)
    setSqpSortDir('asc')
  }

  const toggleSignalSort = (column: SignalSortCol) => {
    if (column === signalSortCol) {
      setSignalSortDir((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }

    setSignalSortCol(column)
    setSignalSortDir('asc')
  }

  const exportSqpCsv = () => {
    const rows = sortedKeywordSqp.map((row) => ({
      keyword: row.keyword,
      rank: row.rank,
      buttah_share_pct: row.brandShare,
      competitor_share_pct: row.competitorShare,
      top_competitor_name: row.topCompetitor?.name ?? null,
      top_competitor_asin: row.topCompetitor?.asin ?? null,
      top_competitor_share_pct: row.topCompetitor?.share ?? null,
      week_start: row.weekStart,
    }))

    exportToCSV(rows, `keyword-sqp-${new Date().toISOString().slice(0, 10)}.csv`)
  }

  const exportIntrudersCsv = () => {
    const rows = sortedKeywordSignals.map((row) => ({
      keyword: row.keyword,
      brand: row.brand,
      title: row.title,
      asin: row.asin,
      rank: row.rank,
      first_seen: row.firstSeen,
      appearance_count: row.appearanceCount,
      is_new: row.isNewAdvertiser,
      is_own_listing: Boolean(row.isOwnListing),
    }))

    exportToCSV(rows, `keyword-intruders-${new Date().toISOString().slice(0, 10)}.csv`)
  }

  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Keyword Intelligence</h2>
        <p className="text-sm text-slate-500">Branded share-of-click plus non-Buttah intruders in branded Amazon searches.</p>
      </div>

      {keywordError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{keywordError}</div>
      )}

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-slate-500" />
            <h3 className="font-semibold text-slate-900">Brand Click Share (SQP)</h3>
          </div>

          <button
            type="button"
            onClick={exportSqpCsv}
            disabled={isLoading || sortedKeywordSqp.length === 0}
            className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>

        {isLoading ? (
          <TableRowSkeleton />
        ) : sortedKeywordSqp.length === 0 ? (
          <EmptyState
            icon={<Search className="h-6 w-6" />}
            title="No keyword intel yet"
            description="Keyword share rows will appear as SQP and intruder tracking data is ingested."
          />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <SortableHeader
                    label="Keyword"
                    column="keyword"
                    activeColumn={sqpSortCol}
                    direction={sqpSortDir}
                    onToggle={toggleSqpSort}
                  />
                  <SortableHeader
                    label="Amazon Rank"
                    column="rank"
                    activeColumn={sqpSortCol}
                    direction={sqpSortDir}
                    onToggle={toggleSqpSort}
                  />
                  <SortableHeader
                    label="Buttah Share"
                    column="brandShare"
                    activeColumn={sqpSortCol}
                    direction={sqpSortDir}
                    onToggle={toggleSqpSort}
                  />
                  <SortableHeader
                    label="Competitor Share"
                    column="competitorShare"
                    activeColumn={sqpSortCol}
                    direction={sqpSortDir}
                    onToggle={toggleSqpSort}
                  />
                  <SortableHeader
                    label="Top Competitor"
                    column="topCompetitor"
                    activeColumn={sqpSortCol}
                    direction={sqpSortDir}
                    onToggle={toggleSqpSort}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {sortedKeywordSqp.map((row) => {
                  const showTopCompetitor = (row.competitorShare ?? 0) > 0 && row.topCompetitor
                  const buttahPct = clampPercentage(row.brandShare)
                  const competitorPct = clampPercentage(row.competitorShare)
                  const othersPct = Math.max(0, 100 - buttahPct - competitorPct)

                  return (
                    <tr key={row.keyword}>
                      <td className="px-3 py-2 text-slate-900">{row.keyword}</td>
                      <td className="px-3 py-2 text-slate-700">{row.rank !== null ? `#${row.rank.toLocaleString('en-US')}` : '—'}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="flex h-2 w-24 overflow-hidden rounded-full">
                            <div className={shareBarColor(row.brandShare)} style={{ width: `${buttahPct}%` }} />
                            <div className={competitorShareBarColor(row.competitorShare)} style={{ width: `${competitorPct}%` }} />
                            <div className="bg-slate-200" style={{ width: `${othersPct}%` }} />
                          </div>
                          <span className="text-xs text-slate-600">{formatPercent(row.brandShare)}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        <span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-semibold ${shareToneClasses(row.competitorShare)}`}>
                          {formatPercent(row.competitorShare)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {showTopCompetitor ? (
                          <div className="space-y-0.5">
                            <div className="font-medium text-slate-900">{row.topCompetitor?.name || 'Unnamed competitor'}</div>
                            <div className="text-xs text-slate-500">
                              {row.topCompetitor?.asin ? `ASIN ${row.topCompetitor.asin}` : 'ASIN unavailable'}
                              {row.topCompetitor?.share !== null ? ` • ${formatPercent(row.topCompetitor?.share ?? null)}` : ''}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-emerald-600">No intruders</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-slate-500" />
            <div>
              <h3 className="font-semibold text-slate-900">Branded Search Intruders</h3>
              <p className="text-xs text-slate-500">
                {competitorIntruders.length.toLocaleString('en-US')} competitor intruders •{' '}
                {ownListings.length.toLocaleString('en-US')} own listings (expected)
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={exportIntrudersCsv}
            disabled={isLoading || sortedKeywordSignals.length === 0}
            className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>

        {isLoading ? (
          <TableRowSkeleton />
        ) : competitorIntruders.length === 0 ? (
          <EmptyState
            icon={<ShieldAlert className="h-6 w-6" />}
            title="No intruders detected"
            description="Non-Buttah branded-search intruders will show here once captured."
          />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <SortableHeader
                    label="Keyword"
                    column="keyword"
                    activeColumn={signalSortCol}
                    direction={signalSortDir}
                    onToggle={toggleSignalSort}
                  />
                  <th className="px-3 py-2 font-medium">Type</th>
                  <SortableHeader
                    label="Brand"
                    column="brand"
                    activeColumn={signalSortCol}
                    direction={signalSortDir}
                    onToggle={toggleSignalSort}
                  />
                  <SortableHeader
                    label="Product"
                    column="title"
                    activeColumn={signalSortCol}
                    direction={signalSortDir}
                    onToggle={toggleSignalSort}
                  />
                  <SortableHeader
                    label="Position"
                    column="rank"
                    activeColumn={signalSortCol}
                    direction={signalSortDir}
                    onToggle={toggleSignalSort}
                  />
                  <SortableHeader
                    label="First Seen"
                    column="firstSeen"
                    activeColumn={signalSortCol}
                    direction={signalSortDir}
                    onToggle={toggleSignalSort}
                  />
                  <SortableHeader
                    label="Times Seen"
                    column="appearanceCount"
                    activeColumn={signalSortCol}
                    direction={signalSortDir}
                    onToggle={toggleSignalSort}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {competitorIntruders.map((row) => {
                  const newEntry = isWithinLastDays(row.firstSeen, 7)

                  return (
                    <tr key={`${row.keyword}-${row.asin ?? 'unknown'}-${row.rank ?? 'na'}`}>
                      <td className="px-3 py-2 text-slate-700">{row.keyword}</td>
                      <td className="px-3 py-2">
                        <span className="inline-flex rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">
                          🔴 Competitor
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{row.brand || 'Unknown'}</td>
                      <td className="px-3 py-2 text-slate-700" title={row.title ?? ''}>
                        {truncateText(row.title, 80)}
                      </td>
                      <td className="px-3 py-2 text-slate-700">{row.rank !== null ? `#${row.rank}` : '—'}</td>
                      <td className="px-3 py-2 text-slate-700">
                        <div className="inline-flex items-center gap-2">
                          <span>{formatDate(row.firstSeen)}</span>
                          {newEntry ? (
                            <span className="rounded-md border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-xs font-semibold tracking-wide text-blue-700">
                              NEW
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{Math.max(0, row.appearanceCount).toLocaleString('en-US')}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {!isLoading && ownListings.length > 0 ? (
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-blue-500" />
            <h3 className="font-semibold text-slate-900">Your own products appearing here (expected)</h3>
          </div>

          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Keyword</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">Brand</th>
                  <th className="px-3 py-2 font-medium">Product</th>
                  <th className="px-3 py-2 font-medium">Position</th>
                  <th className="px-3 py-2 font-medium">First Seen</th>
                  <th className="px-3 py-2 font-medium">Times Seen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {ownListings.map((row) => (
                  <tr key={`own-${row.keyword}-${row.asin ?? 'unknown'}-${row.rank ?? 'na'}`}>
                    <td className="px-3 py-2 text-slate-700">{row.keyword}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
                        🔵 Own Listing
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-700">{row.brand || 'Unknown'}</td>
                    <td className="px-3 py-2 text-slate-700" title={row.title ?? ''}>
                      {truncateText(row.title, 80)}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{row.rank !== null ? `#${row.rank}` : '—'}</td>
                    <td className="px-3 py-2 text-slate-700">{formatDate(row.firstSeen)}</td>
                    <td className="px-3 py-2 text-slate-700">{Math.max(0, row.appearanceCount).toLocaleString('en-US')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </section>
  )
}
